import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEOHead from "@/components/base/SEOHead";
import Breadcrumb from "@/components/base/Breadcrumb";
import CategoryTabs from "@/components/feature/CategoryTabs";
import VideoCard from "@/components/feature/VideoCard";
import { categories, videos } from "@/mocks/data";

export default function CategoryPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState(searchParams.get("tab") || "all");
  const [displayCount, setDisplayCount] = useState(12);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && tab !== activeCategory) {
      setActiveCategory(tab);
    }
  }, [searchParams]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [activeCategory]);

  const handleCategoryChange = (id: string) => {
    setActiveCategory(id);
    setDisplayCount(12);
    setSearchParams(id === "all" ? {} : { tab: id });
  };

  const filteredVideos = activeCategory === "all"
    ? videos
    : videos.filter((v) => v.category === activeCategory);

  const displayedVideos = filteredVideos.slice(0, displayCount);
  const hasMore = displayCount < filteredVideos.length;
  const activeCatName = categories.find((c) => c.id === activeCategory)?.name || "全部";

  const categorySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${activeCatName} - 快猫视频`,
    description: `快猫视频${activeCatName}分类，浏览精彩短视频内容`,
    url: `https://www.kuaimaovideo.com/category${activeCategory !== "all" ? `?tab=${activeCategory}` : ""}`,
  };

  return (
    <>
      <SEOHead
        title={`${activeCatName}视频分类`}
        description={`快猫视频${activeCatName}分类，浏览海量精彩短视频内容，发现你感兴趣的优质视频。`}
        keywords={`快猫视频,${activeCatName},短视频,视频分类`}
        schema={categorySchema}
      />
      <Breadcrumb items={[{ label: t("category.title") }]} />

      <section className="py-8 md:py-12 bg-background-50">
        <div className="container-page">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
              {t("category.title")}
            </h1>
            <p className="text-foreground-500">{t("category.desc")}</p>
          </div>

          <div className="mb-8">
            <CategoryTabs
              categories={categories.map((c) => ({ id: c.id, name: c.name, icon: c.icon }))}
              activeCategory={activeCategory}
              onSelect={handleCategoryChange}
            />
          </div>

          {displayedVideos.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {displayedVideos.map((video) => (
                  <VideoCard key={video.id} {...video} />
                ))}
              </div>
              {hasMore && (
                <div className="mt-10 text-center">
                  <button
                    onClick={() => setDisplayCount((prev) => prev + 12)}
                    className="px-8 py-3 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {t("category.loadMore")} ({filteredVideos.length - displayCount} {t("category.videoCount")})
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="py-20 text-center">
              <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-vidicon-line text-3xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-400">{t("category.noVideos")}</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}